import { Injectable } from '@angular/core';
import { JSONRPCError } from '@s8l/client-tree-lib';
import { BehaviorSubject } from 'rxjs';
import Swal from 'sweetalert2';

import { Licence, Role, Profile, UserServerService, Template } from './jsonrpc/user-server.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public static readonly licenceUsageInfo = {
    registration: 'Licence for user registration',
    empowerment: 'Licence can be applied by already registered users. It can be used to (temporarily) add additional roles',
    unrestricted:
      'Licence can either be used for registration of new users or to (temporarily) add additional roles to already registered users'
  };

  public roles: BehaviorSubject<Role[]> = new BehaviorSubject<Role[]>([]);
  public users: BehaviorSubject<Profile[]> = new BehaviorSubject<Profile[]>([]);
  public templates: BehaviorSubject<Licence[]> = new BehaviorSubject<Licence[]>([]);

  constructor(private user: UserServerService) {}

  public init(): Promise<any> {
    return Promise.all([this.loadRoles(), this.loadUsers(), this.loadTemplates()]);
  }

  public loadRoles(): Promise<void> {
    return this.user.roleList().then(roles => this.roles.next(roles.filter(e => e.name !== 'api')));
  }

  public loadUsers(): Promise<void> {
    return this.user
      .userList()
      .then(users => this.users.next(users))
      .catch(err => this.skipPermissionError(err));
  }

  public loadTemplates(): Promise<void> {
    return this.user
      .templateList()
      .then(tmpl => this.templates.next(tmpl))
      .catch(err => this.skipPermissionError(err));
  }

  private skipPermissionError(err: any): void {
    if (err instanceof JSONRPCError && err.code === -32001) return;
    throw err;
  }

  public async createUser(profile: Profile, password?: string): Promise<Profile> {
    const u = await this.user.userCreate({ profile: profile, password: password });
    Object.assign(profile, u);
    this.users.next([u, ...this.users.value]);
    return u;
  }

  public async updateUser(profile: Profile, password?: string, updateUsername?: boolean): Promise<Profile> {
    const u = await this.user.userUpdate({ profile, updateUsername, password });
    const old = this.users.value.filter(o => o.uuid !== u.uuid);
    Object.assign(profile, u);
    this.users.next([profile, ...old]);
    return u;
  }

  public async deleteUser(uuid: string): Promise<void> {
    await this.user.userDelete({ uuid: uuid });
    const old = this.users.value.filter(o => o.uuid !== uuid);
    this.users.next([...old]);
  }

  public async activateLicence(profile: Profile, licenceKey: string): Promise<Profile> {
    const u = await this.user.userActivate({ user: profile.uuid, licence: licenceKey });
    const old = this.users.value.filter(o => o.uuid !== u.uuid);
    Object.assign(profile, u);
    this.users.next([profile, ...old]);
    return u;
  }

  public async createTemplate(template: Template): Promise<Template> {
    const u = await this.user.templateCreate(template);
    Object.assign(template, u);
    this.templates.next([u, ...this.templates.value]);
    return u;
  }

  public async createLicense(template: Licence): Promise<Licence> {
    const u = await this.user.licenceCreate(template);
    Object.assign(template, u);
    this.templates.next([u, ...this.templates.value]);
    return u;
  }

  public async updateTemplate(template: Template): Promise<Template> {
    const u = await this.user.templateUpdate(template);
    const old = this.templates.value.filter(o => o.uuid !== u.uuid);
    Object.assign(template, u);
    this.templates.next([template, ...old]);
    const users = await this.user.userList();
    this.users.next(users);
    return u;
  }

  public async updateLicense(template: Licence): Promise<Licence> {
    const u = await this.user.licenceUpdate(template);
    const old = this.templates.value.filter(o => o.uuid !== u.uuid);
    Object.assign(template, u);
    this.templates.next([template, ...old]);
    const users = await this.user.userList();
    this.users.next(users);
    return u;
  }

  public async deleteTemplate(template: Template): Promise<any> {
    await this.user.templateDelete({ uuid: template.uuid });
    const old = this.templates.value.filter(o => o.uuid !== template.uuid);
    this.templates.next([...old]);
    return await Promise.all([
      this.user.userList().then(u => this.users.next(u)),
      this.user.templateList().then(l => this.templates.next(l))
    ]);
  }

  public async deleteLicense(template: Licence): Promise<any> {
    await this.user.licenceDelete({ uuid: template.uuid });
    const old = this.templates.value.filter(o => o.uuid !== template.uuid);
    this.templates.next([...old]);
    return await Promise.all([
      this.user.userList().then(u => this.users.next(u)),
      this.user.templateList().then(l => this.templates.next(l))
    ]);
  }

  public async createRole(role: Role): Promise<Role> {
    const u = await this.user.roleCreate(role);
    const old = this.roles.value.filter(o => o.name !== u.name);
    this.roles.next([u, ...old]);
    return u;
  }

  public async updateRole(role: Role): Promise<Role> {
    const u = await this.user.roleUpdate(role);
    const old = this.roles.value.filter(o => o.name !== u.name);
    this.roles.next([u, ...old]);
    return u;
  }

  public async deleteRole(name: string): Promise<void> {
    await this.user.roleDelete({ name });
    const old = this.roles.value.filter(o => o.name !== name);
    this.roles.next([...old]);
    const u = this.user.userList();
    const l = this.user.templateList();
    this.users.next(await u);
    this.templates.next(await l);
  }

  public async relatedLicencesForDeletion(username: string): Promise<Licence[]> {
    if (!username) {
      return Promise.resolve([]); // No related licences
    }

    const matches = this.templates.getValue().filter(t => t.username === username && t.key != null);

    if (matches.length == 0) {
      return Promise.resolve([]); // No related licences
    }

    let details = null;
    if (matches.length == 1) {
      details = `The licence key <b style="font-weight: bold;">${matches[0].key}</b> is unique to <b style="font-weight: bold;">${username}</b>.<br/>Do you want to delete the licence as well?`;
    } else {
      const some = matches
        .slice(0, 3)
        .map(t => `<b style="font-weight: bold;">${t.key}</b>`)
        .join(',<br/>');
      const more = matches.length <= 3 ? '' : `and ${matches.length - 3} more`;
      details = `There are <b style="font-weight: bold;">${matches.length}</b> licence keys unique to <b style="font-weight: bold;">${username}</b>.<br/>Do you want to delete the licences <br/>${some}<br/> ${more} as well?`;
    }
    const r = await Swal.fire({
      title: matches.length == 1 ? 'Delete additional Licence?' : `Delete ${matches.length} additional Licences?`,
      html: `<p>${details}</p><p>You can also delete the ${matches.length == 1 ? 'licence' : 'licences'} later manually.</p>`,
      icon: 'question',
      confirmButtonText: 'Yes, delete it!',
      showConfirmButton: true,
      showDenyButton: true,
      showCancelButton: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-success',
        denyButton: 'btn btn-danger',
        cancelButton: 'btn btn-info'
      }
    });
    if (r.isConfirmed) {
      return matches; // Delete account and licences
    } else if (r.isDenied) {
      return []; // Do not delete any licences, but continue with account deletion
    } else {
      return null; // Cancel account deletion
    }
  }
}
